import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, Award, Send, Check, AlertCircle, Sparkles, Clock,
  TrendingUp, Target, Users, Loader2, Copy, Share2
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NominationFormPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading, token } = useAuth();
  const { toast } = useToast();
  
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [shareableLink, setShareableLink] = useState('');
  const [copied, setCopied] = useState(false);
  
  const [selectedCategory, setSelectedCategory] = useState('');
  const [answers, setAnswers] = useState({
    q1_inspiration: '',
    q2_proudest_moment: '',
    q3_top_traits: '',
    q4_build_trust: '',
    q5_complex_challenge: '',
    q6_technology_tools: '',
    q7_unique_approach: '',
    q8_team_contribution: '',
    q9_continuous_learning: '',
    q10_excellence_meaning: ''
  });

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      toast({
        title: 'Authentication Required',
        description: 'Please login to submit your nomination',
        variant: 'destructive'
      });
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  const categories = [
    { key: 'rookie', name: 'Best TA Rookie Award', experience: '6 months – 2 years', icon: Sparkles, color: 'text-green-600', bg: 'bg-green-50' },
    { key: 'emerging_leader', name: 'Best Emerging TA Leader Award', experience: '4 – 8 years', icon: TrendingUp, color: 'text-blue-600', bg: 'bg-blue-50' },
    { key: 'excellence', name: 'Best TA Excellence Award', experience: '2 – 4 years', icon: Target, color: 'text-purple-600', bg: 'bg-purple-50' },
    { key: 'manager', name: 'Best TA Manager Award', experience: '8 – 12 years', icon: Users, color: 'text-orange-600', bg: 'bg-orange-50' },
    { key: 'senior_leader', name: 'Best TA Senior Leader Award', experience: '12+ years', icon: Award, color: 'text-yellow-600', bg: 'bg-yellow-50' }
  ];

  const questions = [
    { id: 'q1_inspiration', label: 'What inspired you to start your journey in Talent Acquisition?', placeholder: 'Share your story of how you entered the TA field...' },
    { id: 'q2_proudest_moment', label: 'Describe your proudest moment or success story as a recruiter or TA leader.', placeholder: 'Tell us about a placement or achievement that made you proud...' },
    { id: 'q3_top_traits', label: 'What are the top three traits that define your approach to recruitment?', placeholder: 'List and explain the traits that make your approach unique...' },
    { id: 'q4_build_trust', label: 'How do you build trust and relationships with candidates and clients?', placeholder: 'Describe your relationship-building strategies...' },
    { id: 'q5_complex_challenge', label: 'Share an example where you solved a complex hiring challenge.', placeholder: 'Explain the challenge, your approach, and the outcome...' },
    { id: 'q6_technology_tools', label: 'How do you leverage technology, tools, or data in your recruitment strategy?', placeholder: 'Describe the tools and technologies you use effectively...' },
    { id: 'q7_unique_approach', label: 'What\'s one thing you\'re doing differently that sets you apart from others?', placeholder: 'Share your unique methods or innovations...' },
    { id: 'q8_team_contribution', label: 'How have you contributed to your team\'s or company\'s overall success?', placeholder: 'Explain your impact on organizational success...' },
    { id: 'q9_continuous_learning', label: 'How do you continuously learn and stay ahead in the evolving TA landscape?', placeholder: 'Share your learning habits and development strategies...' },
    { id: 'q10_excellence_meaning', label: 'What does "excellence in talent acquisition" mean to you personally?', placeholder: 'Define what excellence means in your TA philosophy...' }
  ];

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const isFormValid = () => {
    if (!selectedCategory) return false;
    
    return Object.values(answers).every(answer => 
      answer.trim().length >= 50 && answer.trim().length <= 1000
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isFormValid()) {
      toast({
        title: 'Incomplete Form',
        description: 'Please select a category and answer all questions (50-1000 characters each)',
        variant: 'destructive'
      });
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/awards/nominate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          category: selectedCategory,
          answers: answers
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setShareableLink(data.shareable_link);
        setSubmitted(true);
        toast({
          title: 'Nomination Submitted!',
          description: 'Share your unique link to gather votes'
        });
      } else {
        let msg = data.detail || data.message || 'Submission failed';
        if (Array.isArray(msg)) msg = msg.map((m) => m.msg || JSON.stringify(m)).join('; ');
        if (typeof msg === 'object') msg = msg.message || JSON.stringify(msg);
        throw new Error(msg);
      }
    } catch (error) {
      toast({
        title: 'Submission Failed',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareableLink);
    setCopied(true);
    toast({
      title: 'Link Copied!',
      description: 'Share this link to gather votes for your nomination'
    });
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = () => {
    const text = `I've been nominated for the RecruiterIQ Global TA Awards! Vote for me: ${shareableLink}`;
    if (navigator.share) {
      navigator.share({
        title: 'Vote for my TA Award Nomination',
        text: text,
        url: shareableLink
      });
    } else {
      handleCopyLink();
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-purple-50 to-blue-50">
        <Loader2 className="w-12 h-12 animate-spin text-purple-600" />
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-blue-50 py-12 px-6">
        <div className="max-w-3xl mx-auto">
          <Card className="border-2 border-green-200 shadow-2xl">
            <CardContent className="pt-12 pb-12 text-center">
              <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
                <Check className="w-12 h-12 text-white" />
              </div>
              
              <h1 className="text-4xl font-bold text-slate-900 mb-4">
                Nomination Submitted Successfully! 🎉
              </h1>
              
              <p className="text-lg text-slate-600 mb-8">
                Your journey to recognition has begun. Now it's time to gather votes from your network!
              </p>

              <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-6 mb-6">
                <Label className="text-sm font-semibold text-slate-700 mb-2 block">Your Unique Nomination Link</Label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={shareableLink}
                    readOnly
                    className="flex-1 px-4 py-3 border-2 border-purple-300 rounded-lg bg-white text-slate-700 font-mono text-sm"
                  />
                  <Button onClick={handleCopyLink} className="bg-purple-600 hover:bg-purple-700">
                    {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                <Button
                  onClick={handleShare}
                  size="lg"
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold"
                >
                  <Share2 className="w-5 h-5 mr-2" />
                  Share on Social Media
                </Button>
                <Button
                  onClick={() => navigate('/awards/my-dashboard')}
                  size="lg"
                  variant="outline"
                  className="border-2 border-purple-600 text-purple-600 hover:bg-purple-50 font-bold"
                >
                  <Award className="w-5 h-5 mr-2" />
                  View My Dashboard
                </Button>
              </div>

              <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4">
                <h3 className="font-bold text-yellow-800 mb-2 flex items-center justify-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  Remember: 23 Votes Required to Qualify
                </h3>
                <p className="text-sm text-yellow-700">
                  10 candidates • 5 peers • 5 seniors/juniors • 2 managers • 1 HR
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-blue-50 py-12 px-6">
      <div className="max-w-4xl mx-auto">
        <Button
          onClick={() => navigate('/awards')}
          variant="outline"
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Awards
        </Button>

        {/* Header */}
        <div className="text-center mb-12">
          <Award className="w-16 h-16 mx-auto mb-4 text-purple-600" />
          <h1 className="text-5xl font-bold text-slate-900 mb-4">Submit Your Nomination</h1>
          <p className="text-xl text-slate-600">Share your TA journey and achievements with the community</p>
        </div>

        {/* Category Selection */}
        <Card className="mb-8 shadow-lg">
          <CardHeader>
            <CardTitle className="text-2xl">Step 1: Select Your Category</CardTitle>
            <p className="text-slate-600">Choose the category that matches your experience level</p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {categories.map((category) => {
                const Icon = category.icon;
                return (
                  <button
                    key={category.key}
                    onClick={() => setSelectedCategory(category.key)}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      selectedCategory === category.key
                        ? 'border-purple-500 bg-purple-50 shadow-lg'
                        : 'border-slate-200 hover:border-purple-300 hover:shadow-md'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <Icon className={`w-8 h-8 ${category.color}`} />
                      <div className="flex-1">
                        <h3 className="font-bold text-slate-900 mb-1">{category.name}</h3>
                        <p className="text-sm text-slate-600 flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {category.experience}
                        </p>
                      </div>
                      {selectedCategory === category.key && (
                        <Check className="w-6 h-6 text-purple-600" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Questions Form */}
        <form onSubmit={handleSubmit}>
          <Card className="mb-8 shadow-lg">
            <CardHeader>
              <CardTitle className="text-2xl">Step 2: Answer Reflective Questions</CardTitle>
              <p className="text-slate-600">Each answer should be 50-1000 characters. Be thoughtful and authentic.</p>
            </CardHeader>
            <CardContent className="space-y-8">
              {questions.map((question, idx) => {
                const charCount = answers[question.id].length;
                const isValid = charCount >= 50 && charCount <= 1000;
                
                return (
                  <div key={question.id} className="space-y-2">
                    <Label className="text-lg font-semibold text-slate-900 flex items-start gap-2">
                      <span className="bg-purple-600 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm flex-shrink-0">
                        {idx + 1}
                      </span>
                      <span>{question.label}</span>
                    </Label>
                    <Textarea
                      value={answers[question.id]}
                      onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                      placeholder={question.placeholder}
                      rows={5}
                      maxLength={1000}
                      className="resize-none"
                    />
                    <div className="flex justify-between items-center text-sm">
                      <span className={`${
                        charCount < 50 ? 'text-orange-600' :
                        isValid ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {charCount < 50 ? `${50 - charCount} more characters needed` :
                         isValid ? '✓ Valid' : 'Too long (max 1000)'}
                      </span>
                      <span className="text-slate-500">{charCount}/1000</span>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Submit Button */}
          <Card className="shadow-lg border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-blue-50">
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Ready to Submit?</h3>
                  <p className="text-slate-600">
                    Once submitted, your answers cannot be edited. Make sure everything is perfect!
                  </p>
                </div>
                <Button
                  type="submit"
                  size="lg"
                  disabled={!isFormValid() || submitting}
                  className="bg-purple-600 hover:bg-purple-700 text-white font-bold px-8 py-6 text-lg whitespace-nowrap"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Submit Nomination
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>
      </div>
    </div>
  );
};

export default NominationFormPage;
